import { useCurrentRoute } from "config/routes";
import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "store/hooks";
import * as VehicleSlice from "store/vehicle";

// In document order - the last section whose start has scrolled past the top of
// the scroll container is the one the user is looking at.
const SECTION_IDS = [
  "general-vehicle-form",
  "vehicle-form",
  "finance-vehicle-form",
];

/**
 * Keeps the sidebar selection in sync with the scroll position of the vehicle
 * form.
 *
 * Positions are measured against the scroll container rather than taken from
 * the sections' `offsetHeight`: a section's own height says nothing about where
 * it starts in the scrolled content, so height based thresholds drift apart from
 * the real layout as soon as the sections change.
 */
const useTabChange = () => {
  const dispatch = useDispatch();
  const tab = useSelector((s) => s.vehicle.tab);
  const route = useCurrentRoute();

  const changeActiveTab = useCallback(
    (wrapper: Element) => {
      const wrapperTop = wrapper.getBoundingClientRect().top;
      const stickyNavHeight =
        (document.querySelector("#vehicle-form-navigation") as HTMLElement)
          ?.offsetHeight ?? 0;

      let activeSection: string | null = null;
      SECTION_IDS.forEach((id) => {
        const section = document.getElementById(id);
        if (!section) return;
        const offset = section.getBoundingClientRect().top - wrapperTop;
        // +1 so a section scrolled exactly under the sticky bar still counts.
        if (offset <= stickyNavHeight + 1) activeSection = id;
      });

      if (activeSection && activeSection !== tab) {
        dispatch(VehicleSlice.setTab(activeSection));
      }
    },
    [tab, dispatch]
  );

  useEffect(() => {
    if (route === "readOnlyVehicle") return;

    const wrapper = document.querySelector("#vehicle-form-wrapper");
    if (!wrapper) return;

    // Keep a stable reference, otherwise the listener can never be removed.
    const onScroll = () => changeActiveTab(wrapper);
    wrapper.addEventListener("scroll", onScroll);
    return () => wrapper.removeEventListener("scroll", onScroll);
  }, [route, changeActiveTab]);
};

export default useTabChange;
